'use client'

import { ButtonGhost } from '@fukku/button/Ghost/ButtonGhost'
import { IconDownSmall } from 'icons/components/IconDownSmall'
import { IconDropdownUpSmall } from 'icons/components/IconDropdownUpSmall'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { type PropsWithChildren, useEffect, useRef, useState } from 'react'
import { cx } from 'utils/cx'

import styles from './TruncateText.module.css'

interface TruncateTextProps {
	readonly linesToShow?: number
	readonly showMoreButton?: boolean
}

const DEFAULT_LINES_TO_SHOW = 4

export const TruncateText = ({
	children,
	linesToShow = DEFAULT_LINES_TO_SHOW,
	showMoreButton = false,
}: PropsWithChildren<TruncateTextProps>) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const [isTruncated, setIsTruncated] = useState(false)
	const textRef = useRef<HTMLDivElement>(null)
	const { t } = useLabels()

	const toggleExpand = () => {
		setIsExpanded((prevState) => !prevState)
	}

	const checkTruncation = () => {
		const textElement = textRef.current
		if (textElement) {
			const lineHeight = Number.parseFloat(
				getComputedStyle(textElement).lineHeight
			)
			const margins = Array.from(textElement.children).reduce(
				(acc: number, paragraph: Element) =>
					acc + Number.parseInt(getComputedStyle(paragraph).marginBottom, 10),
				0
			)
			const maxHeight = lineHeight * linesToShow + margins
			setIsTruncated(textElement.scrollHeight > maxHeight)
		}
	}

	useEffect(() => {
		checkTruncation()
		window.addEventListener('resize', checkTruncation)
		return () => {
			window.removeEventListener('resize', checkTruncation)
		}
	}, [children, linesToShow])

	return (
		<>
			<div
				ref={textRef}
				id='truncate-text'
				className={cx(
					styles.textContainer,
					styles.truncate,
					isExpanded && styles.expanded
				)}
				style={{
					WebkitLineClamp: isExpanded ? 'unset' : linesToShow,
				}}
			>
				{children}
			</div>
			{showMoreButton && isTruncated && (
				<ButtonGhost
					onClick={toggleExpand}
					className={styles.more}
					aria-expanded={isExpanded}
					aria-controls='truncate-text'
				>
					{isExpanded ? (
						<>
							{t('pdp.viewLess.text')}
							<IconDropdownUpSmall />
						</>
					) : (
						<>
							{t('pdp.viewMore.text')}
							<IconDownSmall />
						</>
					)}
				</ButtonGhost>
			)}
		</>
	)
}
